// useSpamInbox — Polish #2.
//
// Runs a wider sweep than useConversations (which only checks spam on the
// first ~24 Needs You rows): pulls every open-and-inbound conversation in
// the last 30 days, fetches the latest inbound body for each, and runs the
// shared isLikelySpam matcher. Returns the flagged ones so the /spam audit
// page can render them.
//
// Purely read-only — this hook doesn't mutate any row. Audit actions on
// /spam (open thread, mark resolved) reuse the existing conversation and
// status mutations.
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { isLikelySpam } from '@/lib/spam';
import type {
  ConversationRow,
  MessageExcerpt,
} from '@/lib/hooks/useConversations';

export type SpamAuditRow = ConversationRow & {
  excerpt: string;
  sender_name: string | null;
};

const MS_PER_DAY = 86_400_000;

export function useSpamInbox() {
  return useQuery<SpamAuditRow[]>({
    queryKey: ['spam-inbox'],
    queryFn: async () => runSweep(),
    staleTime: 60_000,
  });
}

async function runSweep(): Promise<SpamAuditRow[]> {
  const since = new Date(Date.now() - 30 * MS_PER_DAY).toISOString();

  // Cast a wider net than the main Standup sweep so the audit view catches
  // spam that landed beyond the first 24 Needs You rows. Include resolved
  // threads too — if Kimi bulk-resolved a batch as spam yesterday, she can
  // still find them here for reference.
  const { data: rows, error } = await supabase
    .from('conversations')
    .select(
      'id, channel, brand, subject, customer_email, status, read_at, last_message_at, last_message_direction, snoozed_until, status_changed_at, resolved_at, intent, intent_confidence, intent_meta, intent_detected_at',
    )
    .eq('last_message_direction', 'inbound')
    .is('merged_into_id', null)
    .in('status', ['open', 'resolved', 'awaiting'])
    .gte('last_message_at', since)
    .order('last_message_at', { ascending: false })
    .limit(200);
  if (error) throw error;
  const convs = (rows ?? []) as ConversationRow[];
  if (convs.length === 0) return [];

  // Fetch latest inbound per conversation. One round trip, then we pick
  // the first (newest) row per conversation_id thanks to the desc order.
  const ids = convs.map((c) => c.id);
  const { data: msgs, error: msgErr } = await supabase
    .from('messages')
    .select('conversation_id, body, sender_name, direction, sent_at')
    .in('conversation_id', ids)
    .eq('direction', 'inbound')
    .order('sent_at', { ascending: false });
  if (msgErr) throw msgErr;

  const excerptByConv = new Map<string, MessageExcerpt>();
  for (const m of msgs ?? []) {
    if (!excerptByConv.has(m.conversation_id)) {
      excerptByConv.set(m.conversation_id, {
        conversation_id: m.conversation_id,
        body: m.body,
        sender_name: m.sender_name,
        direction: m.direction,
      });
    }
  }

  // Run the matcher. Only conversations with a fetched excerpt can be
  // classified; anything missing a body stays out of the audit (we'd rather
  // under-report than fabricate a flag).
  const flagged: SpamAuditRow[] = [];
  for (const c of convs) {
    const ex = excerptByConv.get(c.id);
    if (!ex) continue;
    if (isLikelySpam({ subject: c.subject, body: ex.body })) {
      flagged.push({
        ...c,
        excerpt: ex.body,
        sender_name: ex.sender_name,
      });
    }
  }
  return flagged;
}
